<template>
    <validation-observer ref="refForm">

        <b-form
            @submit.prevent="onSubmit"
            @reset.prevent="resetForm"
        >

            <b-row>
                <b-col md="12">

                    <b-card footer-tag="footer" class="mb-0">

                        <b-row>

                            <!-- PROVIDER NAME -->
                            <b-col md="4" class="mb-2">
                                <label for="name"><strong>NOMBRE DEL PROVEEDOR</strong></label>
                                <validation-provider
                                    #default="{ errors }"
                                    rules="required"
                                    name="nombre del proveedor"
                                >
                                    <b-form-input
                                        id="name"
                                        v-model="formData.name"
                                        placeholder="Ingrese el nombre del proveedor ..."
                                        :state="errors.length > 0 ? false : null"
                                        trim
                                    />

                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-col>

                            <!-- RUC -->
                            <b-col md="4" class="mb-2">
                                <label for="ruc"><strong>RUC</strong></label>
                                <validation-provider
                                    #default="{ errors }"
                                    rules="required"
                                    name="RUC"
                                >
                                    <b-form-input
                                        id="ruc"
                                        v-model="formData.ruc"
                                        placeholder="Ingrese el RUC ..."
                                        :state="errors.length > 0 ? false : null"
                                        trim
                                    />

                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-col>

                            <!-- BUSINESS AREA -->
                            <b-col md="4" class="mb-2">
                                <label for="businessArea"><strong>RUBRO</strong></label>
                                <validation-provider
                                    #default="{ errors }"
                                    rules="required"
                                    name="rubro"
                                >
                                    <v-select
                                        id="businessArea"
                                        v-model="formData.businessArea"
                                        :state="errors.length > 0 ? false : null"
                                        :options="options.businessAreas"
                                    />

                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-col>

                            <!-- START DATE CYCLE -->
                            <b-col md="4" class="mb-2">
                                <label for="nextDateCycle"><strong>PRÓXIMA FECHA DEL CICLO</strong></label>
                                <validation-provider
                                    #default="{ errors }"
                                    rules="required"
                                    name="próxima fecha del ciclo"
                                >
                                    <b-input-group>
                                        <flat-pickr
                                            id="nextDateCycle"
                                            v-model="formData.nextDateCycle"
                                            :state="errors.length > 0 ? false : null"
                                            class="form-control"
                                            :config="onlyDateConfig"
                                            placeholder="YYYY-MM-DD"
                                        />
                                        <b-input-group-append>
                                            <b-button
                                                variant="primary"
                                                @click="clearDate('nextDateCycle')"
                                            >
                                                <feather-icon
                                                    icon="XIcon"
                                                    size="12"
                                                />
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>

                                    <small class="text-danger">{{ errors[0] }}</small>
                                    <small
                                        v-if="cycleDateWarning"
                                        class="text-warning d-block mt-1"
                                    >
                                        <feather-icon icon="AlertTriangleIcon" size="12" />
                                        {{ cycleDateWarning }}
                                    </small>
                                </validation-provider>
                            </b-col>

                            <!-- EMAIL -->
                            <b-col md="4" class="mb-2">
                                <label for="email"><strong>CORREO ELECTRÓNICO</strong></label>
                                <validation-provider
                                    #default="{ errors }"
                                    rules="required|email"
                                    name="correo electrónico"
                                >
                                    <b-form-input
                                        id="email"
                                        v-model="formData.email"
                                        placeholder="Ingrese el correo electrónico ..."
                                        :state="errors.length > 0 ? false : null"
                                        trim
                                    />

                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-col>

                            <!-- SECONDARY EMAIL -->
                            <b-col md="4" class="mb-2">
                                <label for="secondaryEmail"><strong>CORREO SECUNDARIO</strong></label>
                                <validation-provider
                                    #default="{ errors }"
                                    rules="email"
                                    name="correo secundario"
                                >
                                    <b-form-input
                                        id="secondaryEmail"
                                        v-model="formData.secondaryEmail"
                                        placeholder="Ingrese el correo secundario (opcional) ..."
                                        :state="errors.length > 0 ? false : null"
                                        trim
                                    />

                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-col>

                            <!-- CREDENTIALS STATUS -->
                            <b-col md="4" class="mb-2 d-flex align-items-center">
                                <b-form-checkbox
                                    :checked="credentialsSent"
                                    disabled
                                >
                                    {{ credentialsSent ? 'Las credenciales ya fueron enviadas' : 'Las credenciales aún no han sido enviadas' }}
                                </b-form-checkbox>
                            </b-col>

                            <!-- CONTRACT NUMBERS -->
                            <b-col md="12" class="mb-1">
                                <label for="contractNumbers"><strong>NÚMERO(S) DE CONTRATO(S)</strong></label>
                                <b-form-textarea
                                    id="contractNumbers"
                                    v-model="formData.contractNumbers"
                                    class="char-textarea"
                                    placeholder="Número(s) de contrato(s) ..."
                                    rows="3"
                                />
                                <small class="textarea-counter-value float-right">
                                    <strong class="char-count">Caracteres: {{ formData.contractNumbers ? formData.contractNumbers.length : 0 }}</strong>
                                </small>
                            </b-col>
                            
                            <!-- CONTRACT EXPIRATIONS -->
                            <b-col md="12">
                                <label for="contractExpirations"><strong>FECHA(S) DE VENCIMIENTO DE LOS CONTRATOS</strong></label>
                                <b-form-input 
                                    id="contractExpirations" 
                                    v-model="formData.contractExpirations" 
                                    trim
                                />
                            </b-col>

                        </b-row>

                        <template #footer>

                            <b-row>

                                <b-col cols="12">

                                    <!-- BUTTON SUBMIT -->
                                    <b-button-loading 
                                        text="ACTUALIZAR" 
                                        type="submit"
                                        :processing="processing">
                                    </b-button-loading>

                                </b-col>

                            </b-row>

                        </template>

                    </b-card>
                    
                </b-col>
            </b-row>

        </b-form>

    </validation-observer>
</template>

<script>

    import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
    import BButtonLoading from '@core/components/b-button-loading/BButtonLoading.vue';
    import { ref, computed, onBeforeMount, onUnmounted } from '@vue/composition-api';
    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import providerStoreModule from '../../providerStoreModule';
    import { useToast } from 'vue-toastification/composition';
    import { Spanish } from 'flatpickr/dist/l10n/es';
    import flatPickr from 'vue-flatpickr-component';
    import { required, email as emailRule } from '@validations';
    import { SPECIAL_MONTHS, getTypeForDate } from '@core/utils/cycleSchedule';
    import vSelect from 'vue-select';
    import moment from 'moment';
    import store from '@/store';
    import axios from '@axios';

    export default {
        components: {
            ValidationProvider,
            ValidationObserver,

            BButtonLoading,

            flatPickr,
            required,
            emailRule,
            vSelect
        },
        props: {
            providerData: {
                type: Object,
                required: true
            }
        },
        setup(props) {

            // USE TOAST
            const toast = useToast();

            // REFS
            const refForm = ref(null);

            const providerData = ref(props.providerData);

            const onlyDateConfig = ref({
                enableTime: false,
                locale: Spanish,
                minDate: moment().add(1, 'days').format('YYYY-MM-DD')
            });

            const formData = ref({
                name: null,
                ruc: null,
                businessArea: null,
                nextDateCycle: null,
                contractNumbers: null,
                contractExpirations: null,
                email: null,
                secondaryEmail: null
            });

            const formActiveMonths = ref([]);

            const cycleDateWarning = computed(() => {
                const date = formData.value.nextDateCycle;
                if (!date) return null;

                const m = moment(date);
                const monthKey = m.format('YYYY-MM');
                const month = m.month() + 1;
                const day = m.date();
                const targetType = getTypeForDate(date);

                // A) Provider already has a form of that type for that month
                if ((formActiveMonths.value || []).some(
                    e => e.month === monthKey && e.type === targetType
                )) {
                    return `Ya existe un formulario ${targetType} en ese mes; modifique la fecha para el siguiente ciclo.`;
                }

                // B) Date falls in a special month but not on a canonical slot
                if (SPECIAL_MONTHS[month]) {
                    const slots = SPECIAL_MONTHS[month];
                    const matches = slots.some(s => s.day === day);
                    if (!matches) {
                        const list = slots
                            .map(s => moment({ year: m.year(), month: month - 1, day: s.day })
                                .format('DD/MM/YYYY') + ` (${s.type})`)
                            .join(' y ');
                        return `Las fechas oficiales de ese mes son ${list}; la fecha ingresada las reemplazará.`;
                    }
                }

                return null;
            });

            const credentialsSent = ref(false);

            const processing = ref(false);
            const options = ref({});

            const PROVIDER_APP_STORE_MODULE_NAME = 'app-provider';

			// REGISTER MODULE
			if (!store.hasModule(PROVIDER_APP_STORE_MODULE_NAME)) store.registerModule(PROVIDER_APP_STORE_MODULE_NAME, providerStoreModule);

			// UNREGISTER ON LEAVE
			onUnmounted(() => {
				if (store.hasModule(PROVIDER_APP_STORE_MODULE_NAME)) store.unregisterModule(PROVIDER_APP_STORE_MODULE_NAME);
			});

            // CALL FUNCTION TO POPULATE OPTIONS FOR THE FILTERS
            onBeforeMount( async () => {
                await getSelectFilters();
                setData();
            });

            const getSelectFilters = async () => {
                const { data } = await axios.get('/selects/business-areas');
                options.value = data;
            };

            const setData = () => {
                const businessArea = options.value.businessAreas.find(item => item.value === providerData.value.business_area_id);

                const user = providerData.value.user;

                formData.value = {
                    name: providerData.value.name,
                    ruc: providerData.value.ruc,
                    businessArea,
                    nextDateCycle: providerData.value.next_date_cycle,
                    contractNumbers: providerData.value.contract_numbers,
                    contractExpirations: providerData.value.contract_expirations,
                    email: user ? user.email : null,
                    secondaryEmail: user ? user.secondary_email : null
                }

                formActiveMonths.value = providerData.value.form_active_months || [];

                credentialsSent.value = user ? !!user.credentials_sent : false;
            };

            const clearDate = (index) => {
                formData.value[index] = null;
            };

            const onSubmit = async () => {

                const isValid = await refForm.value.validate();

                if (!isValid)
                {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: 'Complete los campos requeridos.',
                            icon: 'AlertTriangleIcon',
                            variant: 'warning'
                        }
                    });
                    
                    return;
                }

                processing.value = true;

                const id = providerData.value.id;

                const payload = {
                    name: formData.value.name,
                    ruc: formData.value.ruc,
                    business_area_id: formData.value.businessArea.value,
                    next_date_cycle: formData.value.nextDateCycle,
                    contract_numbers: formData.value.contractNumbers,
                    contract_expirations: formData.value.contractExpirations,
                    email: formData.value.email,
                    secondary_email: formData.value.secondaryEmail
                }

                store.dispatch('app-provider/updateProvider', { id, payload })
                    .then( response => {

                        formData.value.name = formData.value.name.toUpperCase();

                        toast({
                            component: ToastificationContent,
                            props: {
                                title: response.data.message,
                                icon: 'CheckIcon',
                                variant: 'success'
                            }
                        });

                    })
                    .catch( (err) => {
                        let message = err.response.data.message ? err.response.data.message : 'Error al actualizar la información general del proveedor.';
                        
                        toast({
                            component: ToastificationContent,
                            props: {
                                title: message,
                                icon: 'AlertTriangleIcon',
                                variant: 'danger'
                            }
                        });
                    })
                    .finally( () =>  processing.value = false);

            };

            return {
                // DATA
                onlyDateConfig,
                credentialsSent,
                processing,
                formData,
                options,
                cycleDateWarning,

                // REFS
                refForm,

                // METHODS
                clearDate,
                onSubmit
            }
        }
    }

</script>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
    @import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>